// PIN validation. Originally hit validate_pin RPC directly via PostgREST
// anon. Tier 3 (2026-05-01) routes login through the issue-jwt Lambda so
// we get back a signed JWT alongside the user record. The JWT is then
// attached to all subsequent PostgREST requests; RLS policies use its
// claims (`sub`=PIN, `user_role`) for row-level filtering.

import { setJwt } from './awsdb';

const ISSUE_JWT_URL = import.meta.env.VITE_AWS_ISSUE_JWT_URL;

if (!ISSUE_JWT_URL) {
  throw new Error(
    '[auth] VITE_AWS_ISSUE_JWT_URL is not set. ' +
    'Add it to app/.env.local for local dev, or to the frontend build env for deploys.'
  );
}

// Cache the client IP across the session so we don't hit ipify on every
// retry. Failure is silent — IP is audit-only metadata (Activity Log
// "ip_address" column), not security-critical, so a missing IP is fine.
let cachedIp = null;
async function getClientIp() {
  if (cachedIp !== null) return cachedIp;
  try {
    const res = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(2000) });
    if (!res.ok) throw new Error('ipify status ' + res.status);
    const data = await res.json();
    cachedIp = data?.ip || '';
  } catch {
    cachedIp = '';
  }
  return cachedIp;
}

// Returns { data, error } shape so call-sites that already destructure
// from the old PostgREST RPC response don't need to change.
export async function validatePin(pinCode) {
  const client_ip = await getClientIp();
  try {
    const res = await fetch(ISSUE_JWT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin: pinCode, client_ip: client_ip || null }),
    });

    if (res.status === 401) return { data: null, error: null };

    if (res.status === 429) {
      const j = await res.json().catch(() => ({}));
      return { data: { locked: true, retry_after_seconds: j.retry_after_seconds || 900 }, error: null };
    }

    if (!res.ok) {
      return { data: null, error: { message: 'login service unavailable' } };
    }

    const data = await res.json();
    if (data?.jwt) setJwt(data.jwt);
    return { data, error: null };
  } catch (e) {
    return { data: null, error: { message: e?.message || 'login network error' } };
  }
}
